/** One way to advance a solo run: the api's answer is the run's new state, so it goes into the resume cache and the team's state is read again, whichever control moved the run (Continue, the halted card's Continue, Submit). */
import { useCallback, useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { BodyOf, SoloRunState, TeamState } from "@bsim/contracts";
import { useApi } from "../api/context";
import type { ApiResult, RouteCall } from "../api/types";

type Advance = (body: BodyOf<"soloAdvance">, onOk?: (next: SoloRunState) => void) => Promise<ApiResult<SoloRunState>>;

// One advance in flight per run, whichever control sent it (Results has a Continue in the header and one at the foot): every control of the run reads the same `pending`, and a second press joins the first.
const inFlight = new Map<string, Promise<ApiResult<SoloRunState>>>();
const listeners = new Set<() => void>();
const announce = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** The pill reads the round's phase from the team state; the reply already says it, so the cached copies take it now rather than when the slower read lands. */
function patchPhase(queryClient: QueryClient, phase: SoloRunState["phase"]): void {
  queryClient.setQueriesData<TeamState>({ queryKey: ["teamState"] }, (old) => (old?.round == null ? old : { ...old, round: { ...old.round, soloPhase: phase } }));
}

export function useSoloAdvance(runId: string, call: RouteCall<"soloAdvance">): { readonly pending: boolean; readonly go: Advance } {
  const queryClient = useQueryClient();
  const api = useApi();
  const pending = useSyncExternalStore(subscribe, () => inFlight.has(runId), () => false);
  const go = useCallback<Advance>((body, onOk) => {
    const running = inFlight.get(runId);
    if (running !== undefined) return running;
    const run = (async (): Promise<ApiResult<SoloRunState>> => {
      let r = await call({ runId }, body);
      if (!r.ok && r.error.code === "INVALID_TRANSITION" && body.action === "nextRound") {
        // The run may already have moved on (an earlier press landed, or the page is stale): read it, and if it is past this round, go where it is.
        const now = await queryClient.fetchQuery({ queryKey: ["soloResume", { runId }], staleTime: 0, queryFn: async () => {
          const got = await api.call("soloResume", { runId }, undefined as never);
          if (!got.ok) throw new Error(got.error.message);
          return got.value;
        } }).catch(() => null);
        if (now !== null && now.round.id !== body.fromRoundId) r = { ok: true, value: now };
      }
      if (!r.ok) return r;
      patchPhase(queryClient, r.value.phase);
      // The next year's state is read before leaving: the Outlook it opens on mounts onto a team state that is already fresh and active, not one a reset is still dropping.
      if (body.action === "nextRound") await queryClient.invalidateQueries({ queryKey: ["teamState"], refetchType: "all" });
      // Leave first (the caller's `onOk`): the page being left reads the resume cache, and the moment it holds the new phase the run page may send a deciding run on ahead of where the caller means to go.
      onOk?.(r.value);
      queryClient.setQueryData(["soloResume", { runId }], r.value);
      await queryClient.invalidateQueries({ queryKey: ["soloResume"], refetchType: "all" });
      // Submit releases the round's draft: reset, not just stale, so a Decide page opened from a cached copy does not show the released round. Continue leaves the round as it was; the patched phase stands while the read lands.
      if (body.action === "submit") await queryClient.resetQueries({ queryKey: ["teamState"] });
      else if (body.action === "continue") await queryClient.invalidateQueries({ queryKey: ["teamState"] });
      return r;
    })().finally(() => { inFlight.delete(runId); announce(); });
    inFlight.set(runId, run);
    announce();
    return run;
  }, [api, call, queryClient, runId]);
  return { pending, go };
}
